/**
 * `useDialogStackEntry` — keep this dialog registered on the shared stack while it is open.
 *
 * Extracted from `useModalChrome` because adding the stack took that hook to 13 hooks and 5
 * effects, past `web/excessive-hooks-usage`'s 12/4 thresholds. That rule is STRUCTURAL: it may not
 * be suppressed, with or without a justification (GEN0-STRUCTURAL-ELIMINATION, which audited ~94
 * such justifications and found ~94 false). Decomposing is the only move, and it is the right one
 * here anyway — "am I the dialog the user is looking at" is a separate concern from "what happens
 * to focus and scroll because a dialog opened".
 *
 * ## Why the registered entry has to be STABLE, and why it uses getters
 *
 * The stack holds one entry object for the dialog's entire open life, so an entry that captured
 * the first render's `onClose` would call a stale closure — the dialog would close something, or
 * nothing, based on state from whenever it opened. Re-registering on every render would fix that
 * and break ordering instead: re-registration moves the entry to the top, so an outer dialog that
 * happened to re-render would steal Escape from the inner one.
 *
 * So: the object identity is fixed at first render, and its fields are GETTERS reading a ref that
 * every render refreshes. Stable to the stack, current to the handler.
 *
 * @module @puffin-core/dialog-chrome/useDialogStackEntry
 */

import { useEffect, useRef } from 'react';
import { registerDialog } from './dialogStack.js';

/**
 * @param {Object} params
 * @param {boolean} params.isOpen
 * @param {Function} [params.onClose]
 * @param {boolean} params.closeOnEscape
 * @param {HTMLElement|null} params.container - The dialog's own element, once React has attached it.
 * @returns {void}
 */
export function useDialogStackEntry({ isOpen, onClose, closeOnEscape, container }) {
  // No dep array on purpose: this must track EVERY render, because any of the three can change
  // without the dialog opening or closing (a new `onClose` closure, a container that mounts a
  // commit late, a dialog that becomes non-dismissible mid-flight).
  const latestRef = useRef({ onClose, closeOnEscape, container });
  useEffect(() => {
    latestRef.current = { onClose, closeOnEscape, container };
  });

  const entryRef = useRef(null);
  if (entryRef.current === null) {
    entryRef.current = {
      get onClose() {
        return latestRef.current.onClose;
      },
      get closeOnEscape() {
        return latestRef.current.closeOnEscape;
      },
      getContainer: () => latestRef.current.container,
    };
  }

  // Keyed on `isOpen` ALONE. Registration order is what "top" means, so this effect must not
  // re-run for any other reason.
  useEffect(() => {
    if (!isOpen) return undefined;
    return registerDialog(entryRef.current);
  }, [isOpen]);
}

export default useDialogStackEntry;
